import { Check, Lock, Pencil, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'

import { Button, Input, Select } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import {
  CATEGORY_LABEL_KEY,
  CATEGORY_MEANING_KEY,
  STAGE_COLORS,
  stageColor,
  type StagePatchInput,
  type StageSetting,
} from '../types'

export interface StageRowProps {
  stage: StageSetting
  /** El handle ya cableado que entrega `SortableList`. Siempre visible. */
  handle: ReactNode
  isDragging?: boolean
  onSave: (patch: StagePatchInput) => void
  onDelete: () => void
  /** No se borra la última columna de un set: el tablero se quedaría sin ninguna. */
  /**
   * Quedaría `false` si el set tuviera una sola columna. Hoy no cambia nada
   * porque el borrado está deshabilitado de todas formas; se mantiene para
   * cuando el servidor acepte `move_to`.
   */
  canDelete: boolean
  saving?: boolean
}

/**
 * Una columna del tablero, en modo configuración.
 *
 * El nombre y el color se editan; **la categoría no**, y la fila lo dice con un
 * candado y un texto, no solo deshabilitando un control. La diferencia importa:
 * renombrar "En desarrollo" a "Cocinando" es cosmético y seguro, pero la
 * categoría es lo que preguntan el motor de notificaciones y las métricas.
 * Cambiarla en caliente movería los avisos de todo lo que hay adentro.
 */
export function StageRow({
  stage,
  handle,
  isDragging,
  onSave,
  onDelete,
  canDelete,
  saving,
}: StageRowProps) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(stage.name)
  const [color, setColor] = useState(stage.color ?? '')

  const cancel = () => {
    setName(stage.name)
    setColor(stage.color ?? '')
    setEditing(false)
  }

  const save = () => {
    const clean = name.trim()
    if (!clean) return
    onSave({ label: clean, color_token: color === '' ? null : color })
    setEditing(false)
  }

  return (
    <div
      className={cn(
        'hairline-b flex flex-wrap items-center gap-3 bg-surface-1 px-3 py-2.5 last:border-b-0',
        isDragging && 'bg-surface-2',
      )}
    >
      {handle}

      <span
        aria-hidden="true"
        className="h-4 w-[var(--spine-w)] shrink-0 rounded-full"
        style={{ backgroundColor: stageColor(stage) }}
      />

      {editing ? (
        <>
          <Input
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            aria-label={t('settings.stages.nameAria', { columna: stage.name })}
            className="min-w-[160px] flex-1"
            onKeyDown={(event) => {
              if (event.key === 'Enter') save()
              if (event.key === 'Escape') cancel()
            }}
          />

          <Select
            value={color}
            onChange={(event) => setColor(event.target.value)}
            aria-label={t('settings.stages.colorAria', { columna: stage.name })}
            className="w-[150px]"
          >
            <option value="">{t('settings.stages.colorDefault')}</option>
            {STAGE_COLORS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </Select>

          <Button variant="primary" onClick={save} disabled={saving || name.trim() === ''}>
            <Check size={13} aria-hidden="true" />
            {t('common.action.save')}
          </Button>
          <Button
            variant="ghost"
            onClick={cancel}
            aria-label={t('settings.stages.cancelEditAria', { columna: stage.name })}
          >
            <X size={13} aria-hidden="true" />
          </Button>
        </>
      ) : (
        <>
          <span className="min-w-0 flex-1 text-sm text-text-1">{stage.name}</span>

          <span
            title={t(CATEGORY_MEANING_KEY[stage.category])}
            className="hairline inline-flex h-6 shrink-0 items-center gap-1.5 rounded-[var(--radius-control)] px-2 text-2xs text-text-3"
          >
            <Lock size={11} aria-hidden="true" />
            {t(CATEGORY_LABEL_KEY[stage.category])}
          </span>

          {/*
            No hay switch de "terminal": `is_terminal` no existe en el backend.
            Lo que cierra el trabajo es `category === 'done'`, que es además lo
            que leen las notificaciones y las métricas. Se muestra como estado,
            no como interruptor, porque cambiar la categoría de una columna
            mueve las reglas de todo lo que tiene adentro y no es una decisión
            de edición inline.
          */}
          <span
            className="shrink-0 text-2xs text-text-3"
            title={t('settings.stages.closesWorkHint')}
          >
            {stage.category === 'done' ? t('settings.stages.closesWork') : ''}
          </span>

          <span className="num w-[72px] shrink-0 text-right text-2xs text-text-3">
            {stage.items_count === null
              ? t('common.state.none')
              : t('settings.stages.itemCount', { count: stage.items_count })}
          </span>

          <Button
            variant="ghost"
            onClick={() => setEditing(true)}
            aria-label={t('settings.stages.editAria', { columna: stage.name })}
          >
            <Pencil size={13} aria-hidden="true" />
          </Button>

          <Button
            variant="ghost"
            onClick={onDelete}
            disabled={!canDelete}
            title={canDelete ? undefined : t('settings.stages.cannotDeleteLast')}
            aria-label={t('settings.stages.deleteAria', { columna: stage.name })}
          >
            <Trash2 size={13} aria-hidden="true" />
          </Button>
        </>
      )}
    </div>
  )
}
